import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verified, setVerified] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Capture params immediately before any re-renders can clear them
  const codeRef = useRef<string | null>(
    new URLSearchParams(window.location.search).get('code')
  );
  const tokenHashRef = useRef<string | null>(
    new URLSearchParams(window.location.search).get('token_hash')
  );
  const typeRef = useRef<string | null>(
    new URLSearchParams(window.location.search).get('type')
  );

  useEffect(() => {
    const tokenHash = tokenHashRef.current;
    const type = typeRef.current;
    const code = codeRef.current;


    // Token hash flow (works without stored PKCE code verifier)
    if (tokenHash && type) {
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: type as 'recovery' })
        .then(({ error }) => {
          if (error) {
            setExpired(true);
          } else {
            window.history.replaceState({}, '', window.location.pathname);
            setVerified(true);
          }
        });
      return;
    }

    // PKCE code flow (only works if code verifier is in same browser session)
    if (code) {
      supabase.auth.exchangeCodeForSession(code)
        .then(({ error }) => {
          if (error) {
            setExpired(true);
          } else {
            window.history.replaceState({}, '', window.location.pathname);
            setVerified(true);
          }
        });
      return;
    }

    // Fallback: implicit flow via onAuthStateChange
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setVerified(true);
      }
    });

    const timeout = setTimeout(() => {
      setVerified((current) => {
        if (!current) setExpired(true);
        return current;
      });
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMatchError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setMatchError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        setError("Something went wrong. Please try again.");
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="flex justify-center">
            <span className="text-2xl font-bold text-foreground tracking-tight">mySuccession</span>
          </div>
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-foreground font-semibold text-2xl">Password updated</CardTitle>
              <p className="text-muted-foreground text-sm mt-2">
                Your password has been updated. Redirecting to sign in…
              </p>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // Expired / invalid link state
  if (expired && !verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="flex justify-center">
            <span className="text-2xl font-bold text-foreground tracking-tight">mySuccession</span>
          </div>
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle className="text-foreground font-semibold text-2xl">Link expired</CardTitle>
              <p className="text-muted-foreground text-sm mt-2">
                This reset link is invalid or has expired. Request a new one.
              </p>
            </CardHeader>
            <CardContent className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline font-medium"
              >
                Request a new reset link
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading / waiting for verification
  if (!verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="flex justify-center">
            <span className="text-2xl font-bold text-foreground tracking-tight">mySuccession</span>
          </div>
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-foreground font-semibold text-2xl">Verifying…</CardTitle>
              <p className="text-muted-foreground text-sm mt-2">
                Please wait while we verify your reset link.
              </p>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // New password form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex justify-center">
          <span className="text-2xl font-bold text-foreground tracking-tight">mySuccession</span>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-left pb-6">
            <CardTitle className="text-foreground font-semibold text-2xl">Set a new password</CardTitle>
            <p className="text-muted-foreground text-sm">
              Enter your new password below.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-foreground">
                  New password
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={password.length > 0 ? "pr-10" : ""}
                    placeholder="Minimum 8 characters"
                    minLength={8}
                    required
                  />
                  {password.length > 0 && (
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                  Confirm new password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setMatchError(null); }}
                  className="mt-2"
                  placeholder="Re-enter your password"
                  minLength={8}
                  required
                />
                {matchError && (
                  <p className="text-sm text-destructive mt-1">{matchError}</p>
                )}
              </div>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={loading}
                >
                  {loading ? "Updating…" : "Update Password"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
